import {memo, useContext, useEffect, useState} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import AtomButton, {
  atomButtonColors,
  atomButtonDesigns,
  atomButtonShapes,
  atomButtonSizes
} from '@s-ui/react-atom-button'
import AtomImage from '@s-ui/react-atom-image'
import Context from '@s-ui/react-context'
import MoleculeRating, {MoleculeRatingSizes} from '@s-ui/react-molecule-rating'
import {useDelayToNextFrame} from '@adv-mt/mt-hook-use-delay-to-next-frame'
import IconClose from '@adv-mt/mt-svg-icons/lib/Close'
import {useAnalyticsTrack} from '@adv-mt/mt-tracking-use-analytics'

import AppsFlyerSmartScriptLoader from './appsflyerSmartScriptLoader.js'
import {getSmartScriptUrl} from './helpers.js'
import {useCooldownSmartbanner} from './useCooldownSmartbanner.js'

const BASE_CLASS = 'mt-SharedSmartbanner'
const CONTENT_CLASS = `${BASE_CLASS}-content`
const CLOSE_CLASS = `${BASE_CLASS}-close`
const LOGO_CLASS = `${CONTENT_CLASS}Logo`
const INFO_CLASS = `${CONTENT_CLASS}Info`
const TEXT_CLASS = `${INFO_CLASS}Text`
const DESCRIPTION_CLASS = `${INFO_CLASS}Description`
const RATING_CLASS = `${INFO_CLASS}Rating`
const CTA_CLASS = `${BASE_CLASS}-cta`
const BUTTON_CLASS = `${CTA_CLASS}Button`

const APP_STORE_RATING = 4.5

const POSITIONS = {
  RELATIVE: 'relative',
  FIXED: 'fixed',
  STICKY: 'sticky'
}

const PLACEMENTS = {
  TOP: 'top',
  BOTTOM: 'bottom'
}

const SMART_SCRIPT_ONE_LINK_URL = 'https://coches.onelink.me/1823422424'
const DEFAULT_CUSTOM_SMARTBANNER_URL = 'https://coches.onelink.me/1823422424/vioe77bt'

const SharedSmartbanner = ({
  isFullwidth = true,
  pageName,
  placement = PLACEMENTS.BOTTOM,
  position = POSITIONS.FIXED,
  hasAdToolbarOffset,
  hasTopbarOffset,
  hasFiltersOffset,
  source,
  trackingOrigin,
  trackingInformation
}) => {
  const {domain, i18n} = useContext(Context)
  const config = domain.get('config')
  const isMobile = config.get('isMobile')

  const [isSmartScriptLoaded, setSmartScriptLoaded] = useState(false)
  const [targetUrl, setTargetUrl] = useState(DEFAULT_CUSTOM_SMARTBANNER_URL)

  const {track: analyticsTrack} = useAnalyticsTrack()
  const delayToNextFrame = useDelayToNextFrame()
  const {allowRenderBasedOnCooldown, setCooldownPeriod, setAllowRenderBasedOnCooldown} = useCooldownSmartbanner()

  const className = cx(BASE_CLASS, {
    [`${BASE_CLASS}--${position}`]: position,
    [`${BASE_CLASS}--${position}-${placement}`]: position === POSITIONS.FIXED,
    [`${BASE_CLASS}--fullwidth`]: isFullwidth,
    [`${BASE_CLASS}--hasAdToolbarOffset`]: hasAdToolbarOffset,
    [`${BASE_CLASS}--hasTopbarOffset`]: hasTopbarOffset,
    [`${BASE_CLASS}--hasFiltersOffset`]: hasFiltersOffset
  })

  const urlStaticsImages = config.get('URL_STATICS_IMAGES')
  const logoAppsUrl = config.get('LOGO_APPS_IOS_URL')
  const logoUrl = `${urlStaticsImages}${logoAppsUrl}`
  const {IS_SMARTBANNER_APPSFLYER_ENABLED, SMARTBANNER_COOLDOWN_HOURS} = config.get('smartbanner')
  const showSmartbanner = isMobile && IS_SMARTBANNER_APPSFLYER_ENABLED && allowRenderBasedOnCooldown

  const {
    HIT_INFORMATIONS: {SMARTBANNER_CLOSE_ACTION: hitInformationCloseAction},
    TRACKING_EVENT_NAMES: {
      SMARTBANNER_CLICKED: smartbannerClickedEventName,
      SMARTBANNER_VIEWED: smartbannerViewedEventName
    }
  } = config.get('tracking')

  const hitInformation = trackingInformation
  const eventClicked = smartbannerClickedEventName
  const eventViewed = smartbannerViewedEventName

  const {SMARTBANNER_SOURCE_IDS, SMARTBANNER_SOURCES} = config.get('smartbanner')

  const sourceConfig = SMARTBANNER_SOURCES[source] || SMARTBANNER_SOURCES[SMARTBANNER_SOURCE_IDS.DEFAULT]

  const {title, ctaColor, ctaText, description} = sourceConfig

  const handleOnClick = () => {
    window.location.href = targetUrl

    delayToNextFrame(() => {
      const eventData = {
        hit_origin: trackingOrigin,
        hit_information: hitInformation,
        hit_information2: 'install'
      }
      analyticsTrack({event: eventClicked, data: eventData})
    })

    setCooldownPeriod(SMARTBANNER_COOLDOWN_HOURS.AFTER_INSTALL_CLICK)
    setAllowRenderBasedOnCooldown(false)
  }

  const handleOnClose = () => {
    setCooldownPeriod(SMARTBANNER_COOLDOWN_HOURS.DEFAULT)
    setAllowRenderBasedOnCooldown(false)
    delayToNextFrame(() => {
      const eventData = {
        hit_origin: trackingOrigin,
        hit_information: hitInformation,
        hit_information2: hitInformationCloseAction
      }
      analyticsTrack({event: eventClicked, data: eventData})
    })
  }

  const handleOnLoadSmartScript = () => {
    setSmartScriptLoaded(true)
  }

  useEffect(() => {
    if (!showSmartbanner) return

    if (isSmartScriptLoaded && window.AF_SMART_SCRIPT) {
      const smartScriptUrl = getSmartScriptUrl({oneLinkURL: SMART_SCRIPT_ONE_LINK_URL, pageName})
      setTargetUrl(smartScriptUrl?.clickURL)

      if (smartScriptUrl?.clickURL) {
        setTimeout(() => {
          window.AF_SMART_SCRIPT.fireImpressionsLink()
          console.log('AppsFlyer SmartScript impression fired')
        }, 1000)
      }
    }

    delayToNextFrame(() => {
      analyticsTrack({
        event: eventViewed,
        data: {
          hit_origin: trackingOrigin,
          hit_information: hitInformation
        }
      })
    })
  }, [isSmartScriptLoaded]) // eslint-disable-line react-hooks/exhaustive-deps

  return showSmartbanner ? (
    <>
      {!isSmartScriptLoaded && <AppsFlyerSmartScriptLoader onLoad={handleOnLoadSmartScript} />}
      <div className={className}>
        <div className={CLOSE_CLASS} onClick={handleOnClose} role="button" tabIndex={0}>
          <IconClose color="primary" size="small" />
        </div>
        <div className={CONTENT_CLASS}>
          <div className={LOGO_CLASS}>
            <AtomImage height={60} width={60} src={logoUrl} />
          </div>
          <div className={INFO_CLASS}>
            <p className={TEXT_CLASS}>{i18n.t(title)}</p>
            <p className={DESCRIPTION_CLASS}>{i18n.t(description)}</p>
            <div className={RATING_CLASS}>
              <MoleculeRating size={MoleculeRatingSizes.SMALL} value={APP_STORE_RATING} />
            </div>
          </div>
        </div>
        <div className={CTA_CLASS}>
          <div className={BUTTON_CLASS}>
            <AtomButton
              design={atomButtonDesigns.SOLID}
              fullWidth
              color={atomButtonColors[ctaColor]}
              onClick={handleOnClick}
              shape={atomButtonShapes.CIRCULAR}
              size={atomButtonSizes.SMALL}
            >
              {i18n.t(ctaText)}
            </AtomButton>
          </div>
        </div>
      </div>
    </>
  ) : null
}

SharedSmartbanner.propTypes = {
  hasAdToolbarOffset: PropTypes.bool,
  hasTopbarOffset: PropTypes.bool,
  hasFiltersOffset: PropTypes.bool,
  isFullwidth: PropTypes.bool,
  pageName: PropTypes.string,
  placement: PropTypes.oneOf(Object.values(PLACEMENTS)),
  position: PropTypes.oneOf(Object.values(POSITIONS)),
  source: PropTypes.string,
  trackingOrigin: PropTypes.string,
  trackingInformation: PropTypes.string
}

export {PLACEMENTS as sharedSmartbannerPlacements, POSITIONS as sharedSmartbannerPositions}
export default memo(SharedSmartbanner)
